/**
 * Governed Intelligence State — the platform-standard, evidence-driven runtime
 * contract for every intelligence surface (Cinema Intelligence, Audience
 * Intelligence, Campaign Strategy, Cross-Platform Analytics, SHERLOC insight
 * engines, future Pantheon modules).
 *
 * Invariant: no evidence, no insight. An analytical conclusion, status badge,
 * score, risk, or recommendation may render ONLY in READY_FOR_ANALYSIS, and
 * every rendered insight must be traceable to the evidence that produced it.
 * When evidence is absent the surface says so explicitly — it never hides,
 * replaces, approximates, or guesses an insight.
 *
 * State machine (resolved from actual runtime evidence, never assumptions):
 *
 *   UNINITIALIZED → PROCESSING → ERROR
 *                              ↘ NO_DATA
 *                              ↘ INSUFFICIENT_DATA
 *                              ↘ READY_FOR_ANALYSIS
 *
 * This module is intentionally framework-light (pure functions + tiny render
 * helpers) so any page can adopt it without redesign. It must remain
 * byte-identical between the client and superadmin repos (contract-locked by
 * backend/tests/governedIntelligenceStateContract.test.js).
 */

export const GOVERNED_INTELLIGENCE_STATES = Object.freeze({
  UNINITIALIZED: "uninitialized",
  PROCESSING: "processing",
  ERROR: "error",
  NO_DATA: "no_data",
  INSUFFICIENT_DATA: "insufficient_data",
  READY_FOR_ANALYSIS: "ready_for_analysis",
});

const toCount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 0;
};

/**
 * Normalize an evidence source descriptor:
 *   { key, label, count, required }
 * `count` is the number of REAL imported/derived records backing the source
 * (never a default, never a placeholder).
 */
export const normalizeEvidenceSources = (sources = []) =>
  (Array.isArray(sources) ? sources : [])
    .filter((source) => source && typeof source === "object")
    .map((source, index) => ({
      key: String(source.key || `evidence_${index + 1}`),
      label: String(source.label || source.key || `evidence ${index + 1}`),
      count: toCount(source.count),
      required: source.required !== false,
    }));

/**
 * Resolve the governed runtime state from actual evidence.
 *
 *  - `initialized: false`  → UNINITIALIZED (nothing has loaded yet)
 *  - `loading: true`       → PROCESSING (imports/analysis in flight; no
 *                            conclusions may render)
 *  - `error` truthy        → ERROR (honest failure, never a fallback insight)
 *  - every source count 0  → NO_DATA
 *  - a required source 0   → INSUFFICIENT_DATA (only analyses supported by
 *                            present evidence may render)
 *  - otherwise             → READY_FOR_ANALYSIS
 */
export const resolveGovernedIntelligenceState = ({
  initialized = true,
  loading = false,
  error = null,
  evidenceSources = [],
} = {}) => {
  const sources = normalizeEvidenceSources(evidenceSources);
  const presentSources = sources.filter((source) => source.count > 0);
  const missingRequired = sources.filter((source) => source.required && source.count === 0);

  let state;
  if (!initialized) state = GOVERNED_INTELLIGENCE_STATES.UNINITIALIZED;
  else if (loading) state = GOVERNED_INTELLIGENCE_STATES.PROCESSING;
  else if (error) state = GOVERNED_INTELLIGENCE_STATES.ERROR;
  else if (!presentSources.length) state = GOVERNED_INTELLIGENCE_STATES.NO_DATA;
  else if (missingRequired.length) state = GOVERNED_INTELLIGENCE_STATES.INSUFFICIENT_DATA;
  else state = GOVERNED_INTELLIGENCE_STATES.READY_FOR_ANALYSIS;

  return {
    state,
    canRenderConclusions: state === GOVERNED_INTELLIGENCE_STATES.READY_FOR_ANALYSIS,
    evidenceSources: sources,
    presentSources,
    missingRequired,
    evidenceSummary: presentSources
      .map((source) => `${source.count.toLocaleString("en-US")} ${source.label}`)
      .join(" · "),
    missingSummary: missingRequired.map((source) => source.label).join(" · "),
    error: error || null,
  };
};

/** True only when analytical conclusions are allowed to exist. */
export const canRenderGovernedInsight = (intelligence) =>
  Boolean(intelligence && intelligence.state === GOVERNED_INTELLIGENCE_STATES.READY_FOR_ANALYSIS);

/**
 * The single body line for an insight that cannot exist yet, per state. No
 * downstream renderer should invent alternative copy.
 */
export const governedInsightUnavailableCopy = (intelligence, surfaceLabel = "data") => {
  const state = intelligence?.state;
  if (state === GOVERNED_INTELLIGENCE_STATES.PROCESSING) {
    return `Analyzing imported ${surfaceLabel}…`;
  }
  if (state === GOVERNED_INTELLIGENCE_STATES.ERROR) {
    return `This insight cannot be calculated because ${surfaceLabel} failed to load.`;
  }
  if (state === GOVERNED_INTELLIGENCE_STATES.INSUFFICIENT_DATA) {
    return `Additional ${surfaceLabel} is required before this insight can be generated.`;
  }
  return `This insight cannot be calculated because no supporting ${surfaceLabel} has been imported.`;
};

/**
 * Provenance line for a rendered insight — every conclusion must be traceable
 * to the evidence that produced it.
 */
export const governedEvidenceProvenance = (intelligence) =>
  intelligence?.evidenceSummary ? `Generated from ${intelligence.evidenceSummary}.` : "";

/**
 * Guarded value helper: returns the computed conclusion only when evidence
 * supports it, otherwise the governed unavailable copy. Use for narrative
 * strings inside larger layouts.
 */
export const governedConclusion = (intelligence, computeConclusion, surfaceLabel = "data") =>
  canRenderGovernedInsight(intelligence)
    ? computeConclusion()
    : governedInsightUnavailableCopy(intelligence, surfaceLabel);
